import { useCallback, useMemo } from 'react';
import { useSnapshot } from 'valtio';

import {
  APPLET_ACCESS_DENIAL_REASON,
  type AppletPermissionContext,
  type Policy,
  evaluatePolicy,
  isAppletAccessDeniedForReason,
  isAppletAccessFullAllow,
  isAppletAccessLoading,
  isAppletAccessWarnNoApiKey,
} from '@cartwhl/common/appletPolicy';
import * as authStore from '@cartwhl/common/stores/authStore';
import * as userStore from '@cartwhl/common/stores/userStore';
import { getHomepageUrl } from '@cartwhl/common/utils';
import Text from '@cartwhl/ui/base/TextNew';
import Button from '@cartwhl/ui/input/Button';

import useAppletsUrl from '../../hooks/useAppletsUrl';
import MarketingMedia from '../MarketingMedia';
import { pageFrame } from '../page-frame';
import { publicPageGutters } from '../public-page-gutters';
import ProductSpecs from './ProductSpecs';
import {
  cartographerContent,
  motionEditorContent,
  motionGenerationContent,
  performanceCaptureContent,
} from './content';
import type { ProductActionTarget, ProductPageContent } from './types';

const CARTOGRAPHER_APPLET_POLICY = {
  requiresLoggedIn: true,
  requiresApiKey: 'soft',
} satisfies Policy;

const SIGN_UP_THEN_CARTOGRAPHER_REDIRECT = 'apps/cartographer';

interface CartographerCtaProps {
  label: string;
}

function CartographerCta({ label }: CartographerCtaProps) {
  const appletsUrl = useAppletsUrl();
  const cartographerHref = appletsUrl && `${appletsUrl}/cartographer`;
  const authState = useSnapshot(authStore.state);
  const { apiKey, isCartwheeler, loading, userInfo } = useSnapshot(
    userStore.state,
  );
  const accessContext: AppletPermissionContext = useMemo(
    () => ({
      authStatus: authState.authStatus,
      apiKey,
      isCartwheeler,
      userStoreLoading: loading,
      userOrganizationId: userInfo.organizationID ?? '',
    }),
    [
      apiKey,
      authState.authStatus,
      isCartwheeler,
      loading,
      userInfo.organizationID,
    ],
  );
  const access = useMemo(
    () => evaluatePolicy(CARTOGRAPHER_APPLET_POLICY, accessContext),
    [accessContext],
  );
  const openCartographer = useCallback(() => {
    if (isAppletAccessLoading(access) || !cartographerHref) return;

    if (isAppletAccessFullAllow(access) || isAppletAccessWarnNoApiKey(access)) {
      window.open(cartographerHref, '_blank', 'noopener,noreferrer');
      return;
    }

    if (
      isAppletAccessDeniedForReason(
        access,
        APPLET_ACCESS_DENIAL_REASON.anonymous,
      )
    ) {
      window.location.href = getHomepageUrl(
        `/sign-up?redirectTo=${encodeURIComponent(SIGN_UP_THEN_CARTOGRAPHER_REDIRECT)}`,
      );
    }
  }, [access, cartographerHref]);

  return (
    <Button
      type="button"
      variant="cta"
      className="w-fit"
      onClick={openCartographer}
      loading={isAppletAccessLoading(access)}
      disabled={isAppletAccessLoading(access) || !cartographerHref}
    >
      {isAppletAccessFullAllow(access) ? 'Open Cartographer' : label}
    </Button>
  );
}

interface ProductCtaProps {
  action: ProductActionTarget;
  label: string;
}

function ProductCta({ action, label }: ProductCtaProps) {
  const appletsUrl = useAppletsUrl();

  if (action === 'cartographer') {
    return <CartographerCta label={label} />;
  }

  if (action === 'api') {
    return (
      <Button
        as="a"
        href="https://api-docs.getcartwheel.com/"
        target="_blank"
        rel="noopener noreferrer"
        variant="cta"
        className="w-fit"
      >
        {label}
      </Button>
    );
  }

  if (action === 'mcp') {
    return (
      <Button
        as="a"
        href="https://github.com/Cartwhl/cartwheel-mcp"
        target="_blank"
        rel="noopener noreferrer"
        variant="cta"
        className="w-fit"
      >
        {label}
      </Button>
    );
  }

  const href =
    appletsUrl &&
    (action === 'performance-capture'
      ? `${appletsUrl}/comic`
      : action === 'motion-generation'
        ? `${appletsUrl}/generation`
        : `${appletsUrl}/motion-editor`);

  return (
    <Button
      as="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      variant="cta"
      className="w-fit"
      loading={!appletsUrl}
    >
      {label}
    </Button>
  );
}

interface ProductDescriptionProps {
  content: ProductPageContent;
}

function ProductDescription({ content }: ProductDescriptionProps) {
  if (!content.descriptionEmphasis) return content.description;

  const [before, after] = content.description.split(
    content.descriptionEmphasis,
  );

  return (
    <>
      {before}
      <span className="text-primary cw-green-underline">
        {content.descriptionEmphasis}
      </span>
      {after}
    </>
  );
}

interface ProductIntroProps {
  content: ProductPageContent;
}

function ProductIntro({ content }: ProductIntroProps) {
  const widthClassName =
    content.heroWidth === 'wide' ? 'lg:max-w-[984px]' : 'lg:max-w-[813px]';

  return (
    <section
      className={`border-primary border-b pb-10 pt-10 lg:pb-20 lg:pt-[74px] xl:pt-[75px] 2xl:pt-[26px] ${publicPageGutters}`}
    >
      <div className={`flex flex-col gap-10 ${widthClassName}`}>
        <div>
          <Text
            as="p"
            token={{ base: '24/medium', lg: '32/medium' }}
            leading="editorial"
          >
            {content.label}
          </Text>
        </div>
        <Text
          as="h1"
          token="24"
          leading="editorial"
          className="text-secondary text-pretty"
        >
          <ProductDescription content={content} />
        </Text>
        <ProductCta action={content.id} label={content.actionLabel} />
      </div>
    </section>
  );
}

interface ProductFeaturesProps {
  content: ProductPageContent;
}

function ProductFeatures({ content }: ProductFeaturesProps) {
  return (
    <section
      className={`border-primary border-b py-10 lg:py-20 ${publicPageGutters}`}
    >
      <div className="flex flex-col gap-10 lg:gap-20">
        <div>
          <Text
            as="h2"
            token={{ base: '24/medium', lg: '32/medium' }}
            leading="editorial"
          >
            Features
          </Text>
        </div>
        <div className="grid gap-6 min-[960px]:grid-cols-2 min-[960px]:gap-10 min-[1360px]:grid-cols-3">
          {content.features.map((feature) => (
            <article
              key={feature.id}
              className="bg-secondary flex min-w-0 flex-col gap-8 rounded-2xl p-6 xl:gap-10 xl:p-8 2xl:p-12"
            >
              <div className="flex flex-col gap-6">
                <Text
                  as="h3"
                  token={{ base: '24', '2xl': '28' }}
                  leading="editorial"
                  className="text-pretty 2xl:tracking-[-0.02em]"
                >
                  {feature.title}
                </Text>
                <Text
                  as="p"
                  token="16"
                  leading="editorial"
                  className="text-secondary w-full text-pretty tracking-[-0.02em] min-[1360px]:max-w-[275px] 2xl:max-w-[341px]"
                >
                  {feature.description}
                </Text>
              </div>
              <MarketingMedia
                media={feature.media}
                className="mt-auto aspect-video w-full shrink-0 rounded-lg"
              />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

interface MarketingStatementProps {
  content: ProductPageContent;
}

function MarketingStatement({ content }: MarketingStatementProps) {
  const rest = content.statement.slice(content.statementAccent.length);

  return (
    <section
      className={`border-primary border-b py-10 lg:py-20 2xl:py-[82px] ${publicPageGutters}`}
    >
      <Text
        as="h2"
        token={{
          base: 'display/32/bold',
          sm: 'display/40/bold',
          lg: 'display/56/bold',
          xl: 'display/64/bold',
          '2xl': 'display/88/bold',
        }}
        leading="editorial"
        className="max-w-[1546px] tracking-normal [font-feature-settings:'ss03'_1,_'ss01'_1]"
      >
        <span className="text-green">{content.statementAccent}</span>
        {rest}
      </Text>
    </section>
  );
}

interface ProductActionsProps {
  content: ProductPageContent;
}

function ProductActions({ content }: ProductActionsProps) {
  const gridClassName =
    content.actionLayout === 'single-card' ||
    content.actionLayout === 'no-media'
      ? 'grid-cols-1'
      : 'min-[960px]:grid-cols-2';
  const cardAspectClassName =
    content.actionLayout === 'single-card'
      ? 'aspect-square xl:aspect-[224/150] 2xl:aspect-[1824/900]'
      : '';

  return (
    <section
      className={`border-primary border-b py-10 lg:py-20 ${publicPageGutters}`}
    >
      <div
        className={
          content.actionSection
            ? 'flex flex-col gap-10 lg:gap-20'
            : 'flex flex-col'
        }
      >
        {content.actionSection && (
          <header className="flex max-w-[648px] flex-col gap-10">
            <div>
              <Text
                as="h2"
                token={{ base: '24/medium', lg: '32/medium' }}
                leading="editorial"
              >
                {content.actionSection.title}
              </Text>
            </div>
            <Text
              as="p"
              token="24"
              leading="editorial"
              className="text-secondary text-pretty lg:whitespace-pre-line"
            >
              {content.actionSection.description}
            </Text>
          </header>
        )}
        <div className={`grid gap-6 min-[960px]:gap-10 ${gridClassName}`}>
          {content.actionCards.map((card) => (
            <article
              key={card.id}
              className={`bg-secondary flex min-w-0 flex-col gap-8 rounded-2xl p-6 xl:gap-10 xl:p-8 2xl:p-12 ${cardAspectClassName}`}
            >
              <div className="flex flex-col items-start gap-6">
                <Text
                  as="h3"
                  token={{ base: '24', '2xl': '28' }}
                  leading="editorial"
                  className="text-pretty 2xl:tracking-[-0.02em]"
                >
                  {card.title}
                </Text>
                <ProductCta action={card.action} label={card.buttonLabel} />
              </div>
              {card.media && (
                <MarketingMedia
                  media={card.media}
                  className="mt-auto aspect-video w-full shrink-0 rounded-lg"
                />
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

interface ProductPageProps {
  content: ProductPageContent;
}

function ProductPage({ content }: ProductPageProps) {
  const isPerformanceCapture = content.id === 'performance-capture';

  return (
    <div className={pageFrame}>
      <ProductIntro content={content} />
      {isPerformanceCapture ? (
        <PerformanceCaptureStatement content={content} />
      ) : (
        <MarketingStatement content={content} />
      )}
      <ProductFeatures content={content} />
      {content.specs && <ProductSpecs specs={content.specs} />}
      <ProductActions content={content} />
    </div>
  );
}

function PerformanceCaptureStatement({
  content,
}: {
  content: ProductPageContent;
}) {
  const rest = content.statement.slice(content.statementAccent.length);

  return (
    <section
      className={`border-primary border-b py-10 lg:py-20 ${publicPageGutters}`}
    >
      <Text
        as="h2"
        token={{
          base: 'display/32/bold',
          sm: 'display/40/bold',
          lg: 'display/56/bold',
          xl: 'display/64/bold',
          '2xl': 'display/88/bold',
        }}
        leading="editorial"
        className="max-w-[1546px] tracking-normal [font-feature-settings:'ss03'_1,_'ss01'_1]"
      >
        <span className="text-green">{content.statementAccent}</span>
        <span className="text-primary">{rest}</span>
      </Text>
    </section>
  );
}

export function PerformanceCapturePage() {
  return <ProductPage content={performanceCaptureContent} />;
}

export function MotionGenerationPage() {
  return <ProductPage content={motionGenerationContent} />;
}

export function MotionEditorPage() {
  return <ProductPage content={motionEditorContent} />;
}

export function CartographerPage() {
  return <ProductPage content={cartographerContent} />;
}
